import React from 'react';
import { Stethoscope, Activity, Baby, TestTube, Eye, HeartPulse, Syringe, Users, Scissors, Dna, BriefcaseMedical, BrainCircuit, PersonStanding, HeartHandshake, Ear, Wind } from 'lucide-react';
import { ToothIcon } from '../components/icons/ToothIcon';
export interface ServiceData {
  id: string;
  slug: string;
  title: string;
  shortDesc: string;
  icon: React.ReactNode;
  overview: string;
  /** Optional page-specific copy overrides */
  heroHeading?: string;
  intro?: string;
  overviewTitle?: string;
  benefitsTitle?: string;
  ctaTitle?: string;
  ctaLabel?: string;
  whatsappLabel?: string;
  subServices?: string[];
  benefits: string[];
  process: {
    title: string;
    desc: string;
  }[];
  faqs: {
    q: string;
    a: string;
  }[];
}
const generateGenericProcess = (serviceName: string) => [{
  title: 'Initial Consultation',
  desc: `Discuss your medical history and specific needs regarding ${serviceName.toLowerCase()}.`
}, {
  title: 'Assessment & Diagnosis',
  desc: 'Our specialists perform a thorough examination using modern diagnostic tools.'
}, {
  title: 'Treatment Plan',
  desc: 'We develop a personalized care plan tailored to your health goals.'
}, {
  title: 'Follow-up Care',
  desc: 'Ongoing monitoring and support to ensure optimal health outcomes.'
}];
const generateGenericFaqs = (serviceName: string) => [{
  q: `Do I need an appointment for ${serviceName.toLowerCase()}?`,
  a: 'While we accept walk-ins, we highly recommend booking an appointment to minimize your wait time.'
}, {
  q: 'Is this service covered by medical aid?',
  a: 'We accept most major medical aid providers. Please check with our reception for specific coverage details.'
}, {
  q: 'How long does a typical session take?',
  a: 'Most sessions take between 30 to 60 minutes, depending on the complexity of your case.'
}];
export const SERVICES_DATA: ServiceData[] = [{
  id: 'general-consultations',
  slug: 'general-medical-consultations',
  title: 'General Medical Consultations',
  shortDesc: 'Expert medical advice and treatment for common illnesses and health concerns.',
  icon: <Stethoscope className="w-6 h-6" data-id="element-404" data-mp="11leg5re1g2if" />,
  overview: "Our general medical consultations provide comprehensive primary care for patients of all ages. Whether you're dealing with a sudden illness, managing a chronic condition, or seeking preventive care, our experienced doctors are here to listen, diagnose, and treat you with compassion.",
  benefits: ['Comprehensive health assessments', 'Personalized treatment plans', 'Preventive care and health education', 'Referrals to specialists when necessary'],
  process: generateGenericProcess('General Consultations'),
  faqs: generateGenericFaqs('General Consultations')
}, {
  id: 'maternal-care',
  slug: 'antenatal-care',
  title: 'Antenatal Care',
  shortDesc: 'Comprehensive care and monitoring for expectant mothers.',
  icon: <Baby className="w-6 h-6" data-id="element-405" data-mp="3q168yy3542e0" />,
  overview: 'Our antenatal care services are designed to ensure the health and well-being of both mother and baby throughout pregnancy. We provide regular check-ups, ultrasound scans, nutritional advice, and emotional support to prepare you for a safe delivery.',
  benefits: ['Regular health monitoring for mother and baby', 'Early detection of potential complications', 'Nutritional and lifestyle guidance', 'Birth preparedness planning'],
  process: [{
    title: 'First Trimester Booking',
    desc: 'Comprehensive initial assessment, blood tests, and dating scan.'
  }, {
    title: 'Routine Check-ups',
    desc: 'Regular visits to monitor blood pressure, fetal growth, and heartbeat.'
  }, {
    title: 'Anomaly Scan',
    desc: 'Detailed ultrasound at 20-22 weeks to check fetal development.'
  }, {
    title: 'Birth Preparation',
    desc: 'Discussing birth plans and signs of labor in the third trimester.'
  }],
  faqs: [{
    q: 'When should I start antenatal classes?',
    a: 'We recommend starting your antenatal visits as soon as you find out you are pregnant, ideally before 12 weeks.'
  }, {
    q: 'Are ultrasound scans included?',
    a: 'Yes, routine ultrasound scans are part of our comprehensive antenatal care package.'
  }, {
    q: 'Can my partner attend the appointments?',
    a: 'Absolutely. We encourage partners to be involved throughout the pregnancy journey.'
  }]
}, {
  id: 'laboratory-services',
  slug: 'laboratory-services',
  title: 'Laboratory Services',
  shortDesc: 'Accurate and fast diagnostic testing including blood work.',
  icon: <TestTube className="w-6 h-6" data-id="element-406" data-mp="3ha5zni80bhlc" />,
  overview: 'Maisha Medical Center features a state-of-the-art laboratory equipped to perform a wide range of diagnostic tests. Our fast turnaround times ensure that your doctor gets the results needed to make accurate diagnoses and timely treatment decisions.',
  benefits: ['Fast and accurate results', 'Wide range of testing capabilities', 'Strict quality control standards', 'Convenient on-site testing'],
  process: [{
    title: 'Sample Collection',
    desc: 'Our trained phlebotomists safely and comfortably collect your samples.'
  }, {
    title: 'Analysis',
    desc: 'Samples are processed using advanced automated analyzers.'
  }, {
    title: 'Quality Review',
    desc: 'Results are verified by our senior laboratory technicians.'
  }, {
    title: 'Result Delivery',
    desc: 'Results are sent directly to your consulting doctor for review.'
  }],
  faqs: [{
    q: 'Do I need to fast before my blood test?',
    a: 'Some tests require fasting for 8-12 hours. Your doctor will inform you if fasting is necessary for your specific tests.'
  }, {
    q: 'How long do results take?',
    a: 'Many routine tests are available within hours. Specialized tests may take 24-48 hours.'
  }, {
    q: 'Can I get a copy of my results?',
    a: 'Yes, you can request a copy of your results from the reception after your doctor has reviewed them with you.'
  }]
}, {
  id: 'ultrasound',
  slug: 'ultrasound-services',
  title: 'Ultrasound Services',
  shortDesc: 'Advanced imaging for precise medical diagnosis.',
  icon: <Activity className="w-6 h-6" data-id="element-407" data-mp="3fhpx0z0lci4g" />,
  overview: 'We offer advanced diagnostic ultrasound services for obstetrics, gynecology, abdominal, and soft tissue imaging. Our modern equipment provides high-resolution images that help our doctors make accurate and timely diagnoses.',
  benefits: ['Non-invasive and painless imaging', 'No radiation exposure', 'Real-time dynamic imaging', 'Immediate preliminary results'],
  process: generateGenericProcess('Ultrasound Services'),
  faqs: generateGenericFaqs('Ultrasound Services')
}, {
  id: 'minor-surgery',
  slug: 'minor-surgical-procedures',
  title: 'Minor Surgical Procedures',
  shortDesc: 'Safe, in-clinic surgical treatments for minor conditions.',
  icon: <Scissors className="w-6 h-6" data-id="element-408" data-mp="xb5cmtn4w8cp" />,
  overview: 'We perform a variety of minor surgical procedures in our fully equipped procedure rooms under local anesthesia. This includes wound suturing, removal of skin lesions, incision and drainage of abscesses, and more.',
  benefits: ['Performed in a sterile environment', 'Quick recovery times', 'Cost-effective compared to hospital admission', 'Expert surgical care'],
  process: generateGenericProcess('Minor Surgical Procedures'),
  faqs: generateGenericFaqs('Minor Surgical Procedures')
}, {
  id: 'circumcision',
  slug: 'safe-medical-circumcision',
  title: 'Safe Medical Circumcision',
  shortDesc: 'Professional and hygienic male circumcision services.',
  icon: <HeartHandshake className="w-6 h-6" data-id="element-409" data-mp="33q686ri0ks2u" />,
  overview: 'We provide safe, hygienic, and professional medical circumcision services for infants, boys, and adults. Our experienced medical team ensures the procedure is performed with minimal discomfort and provides comprehensive aftercare instructions.',
  benefits: ['Reduced risk of infections', 'Performed under sterile conditions', 'Comprehensive pain management', 'Detailed aftercare support'],
  process: generateGenericProcess('Medical Circumcision'),
  faqs: generateGenericFaqs('Medical Circumcision')
}, {
  id: 'dna-testing',
  slug: 'paternity-testing-dna',
  title: 'Paternity Testing (DNA)',
  shortDesc: 'Highly accurate and confidential DNA testing services.',
  icon: <Dna className="w-6 h-6" data-id="element-410" data-mp="224ro4brbw9r0" />,
  overview: 'Our DNA paternity testing services offer 99.99% accuracy in determining biological relationships. We guarantee strict confidentiality and handle all samples with the utmost professional care and legal compliance if required.',
  benefits: ['99.99% accuracy', 'Strict confidentiality', 'Painless cheek swab collection', 'Fast turnaround times'],
  process: generateGenericProcess('DNA Testing'),
  faqs: generateGenericFaqs('DNA Testing')
}, {
  id: 'immunizations',
  slug: 'immunizations',
  title: 'Immunizations',
  shortDesc: 'Essential vaccines for children and adults.',
  icon: <Syringe className="w-6 h-6" data-id="element-411" data-mp="3l2wkce8mxg7p" />,
  overview: 'Protect yourself and your family from preventable diseases with our comprehensive immunization services. We offer childhood vaccination schedules, travel vaccines, and seasonal flu shots.',
  benefits: ['Disease prevention', 'Safe and effective vaccines', 'Schedules tracked by our clinic', 'Travel health protection'],
  process: generateGenericProcess('Immunizations'),
  faqs: generateGenericFaqs('Immunizations')
}, {
  id: 'pre-employment',
  slug: 'pre-employment-medical-examinations',
  title: 'Pre-Employment Medicals',
  shortDesc: 'Comprehensive health screenings for new employees.',
  icon: <BriefcaseMedical className="w-6 h-6" data-id="element-412" data-mp="1s73bymmsjpv1" />,
  overview: 'We offer thorough pre-employment medical examinations to ensure candidates are medically fit for their prospective roles. Our assessments can be tailored to meet specific industry requirements.',
  benefits: ['Detailed health reports', 'Fast processing for employers', 'Customizable screening panels', 'Occupational health compliance'],
  process: generateGenericProcess('Pre-Employment Medicals'),
  faqs: generateGenericFaqs('Pre-Employment Medicals')
}, {
  id: 'family-planning',
  slug: 'family-planning',
  title: 'Family Planning',
  shortDesc: 'Confidential counseling and contraceptive services.',
  icon: <Users className="w-6 h-6" data-id="element-413" data-mp="s07tj5mafubq" />,
  overview: 'Our family planning clinic provides confidential advice and a wide range of contraceptive options to help you make informed decisions about your reproductive health and family sizing.',
  benefits: ['Wide range of contraceptive options', 'Confidential environment', 'Expert reproductive advice', 'Long-term and short-term solutions'],
  process: generateGenericProcess('Family Planning'),
  faqs: generateGenericFaqs('Family Planning')
}, {
  id: 'counseling',
  slug: 'counseling-services',
  title: 'Counseling Services',
  shortDesc: 'Professional mental health and emotional support.',
  icon: <BrainCircuit className="w-6 h-6" data-id="element-414" data-mp="3lpalqme8kouf" />,
  overview: 'Mental health is a crucial part of overall well-being. Our professional counselors offer a safe, confidential space to discuss and manage stress, anxiety, depression, grief, and relationship issues.',
  benefits: ['Safe, non-judgmental environment', 'Evidence-based therapies', 'Stress and anxiety management', 'Improved emotional well-being'],
  process: generateGenericProcess('Counseling Services'),
  faqs: generateGenericFaqs('Counseling Services')
}, {
  id: 'chronic-disease',
  slug: 'chronic-disease-management',
  title: 'Chronic Disease Management',
  shortDesc: 'Ongoing support for diabetes, hypertension, and more.',
  icon: <HeartPulse className="w-6 h-6" data-id="element-415" data-mp="16o308djq1lfq" />,
  overview: 'Managing a chronic condition requires consistent care and lifestyle adjustments. We provide comprehensive management plans for conditions like diabetes, hypertension, asthma, and HIV, focusing on long-term health and quality of life.',
  benefits: ['Regular monitoring and adjustments', 'Medication management', 'Lifestyle and dietary counseling', 'Prevention of complications'],
  process: generateGenericProcess('Chronic Disease Management'),
  faqs: generateGenericFaqs('Chronic Disease Management')
}, {
  id: 'physiotherapy',
  slug: 'physiotherapy',
  title: 'Physiotherapy',
  shortDesc: 'Rehabilitation and pain management therapies.',
  icon: <PersonStanding className="w-6 h-6" data-id="element-416" data-mp="1omwkjedol4i" />,
  overview: 'Our physiotherapy services aim to restore movement and function following injury, illness, or disability. We use manual therapy, exercise prescription, and electrotherapy to relieve pain and improve mobility.',
  benefits: ['Pain relief without medication', 'Improved mobility and strength', 'Post-surgical rehabilitation', 'Sports injury recovery'],
  process: generateGenericProcess('Physiotherapy'),
  faqs: generateGenericFaqs('Physiotherapy')
}, {
  id: 'optician',
  slug: 'optician-services',
  title: 'Optician Services',
  shortDesc: 'Professional eye examinations and vision care.',
  icon: <Eye className="w-6 h-6" data-id="element-417" data-mp="9s4kbf8ojqgn" />,
  overview: 'Comprehensive eye care services including vision testing, prescription of glasses and contact lenses, and screening for eye diseases such as glaucoma and cataracts.',
  benefits: ['Accurate vision correction', 'Early detection of eye diseases', 'Wide selection of frames', 'Expert optical advice'],
  process: generateGenericProcess('Optician Services'),
  faqs: generateGenericFaqs('Optician Services')
}, {
  id: 'postnatal-care',
  slug: 'postnatal-care',
  title: 'Postnatal Care',
  shortDesc: 'Essential healthcare for mothers and newborns after birth.',
  icon: <Baby className="w-6 h-6" data-id="element-418" data-mp="3e51bzlmvo9lb" />,
  overview: 'The period following birth is critical for both mother and baby. Our postnatal care includes physical check-ups, breastfeeding support, newborn assessments, and monitoring for postpartum depression.',
  benefits: ['Support for physical recovery', 'Breastfeeding guidance', 'Newborn health monitoring', 'Emotional and mental health support'],
  process: generateGenericProcess('Postnatal Care'),
  faqs: generateGenericFaqs('Postnatal Care')
}, {
  id: 'ecg',
  slug: 'ecg-services',
  title: 'ECG Services',
  shortDesc: 'Electrocardiogram testing for heart health assessment.',
  icon: <Activity className="w-6 h-6" data-id="element-419" data-mp="2un5gky5llw0k" />,
  overview: 'An Electrocardiogram (ECG) is a simple, painless test that records the electrical activity of your heart. It helps our doctors diagnose heart conditions, monitor existing heart disease, and ensure your heart is healthy.',
  benefits: ['Quick and painless procedure', 'Immediate results', 'Crucial for diagnosing arrhythmias', 'Non-invasive heart monitoring'],
  process: generateGenericProcess('ECG Services'),
  faqs: generateGenericFaqs('ECG Services')
}, {
  id: 'dentistry',
  slug: 'dentistry',
  title: 'Dentistry',
  shortDesc: 'Comprehensive dental care focused on maintaining healthy teeth, gums, and confident smiles.',
  icon: <ToothIcon className="w-6 h-6" data-id="element-420" data-mp="35u0q0d1mq4d" />,
  overview: 'Our dentistry services cover every stage of oral health, from routine check-ups and cleanings to restorative, cosmetic, and emergency care. Our dental team provides gentle, professional treatment for adults and children in a comfortable, welcoming environment.',
  subServices: ['General Dentistry', 'Dental Cleaning & Oral Hygiene', 'Fillings & Restorative Care', 'Tooth Extractions', "Children's Dentistry", 'Cosmetic Dentistry', 'Emergency Dental Care'],
  benefits: ['Healthy teeth and gums for life', 'Early detection of dental problems', 'Gentle care for children and adults', 'Prompt relief for dental emergencies'],
  process: generateGenericProcess('Dentistry'),
  faqs: generateGenericFaqs('Dentistry')
}, {
  id: 'audiometry',
  slug: 'audiometry',
  title: 'Audiometry',
  shortDesc: 'Hearing assessments that evaluate how well you hear different sounds and frequencies.',
  icon: <Ear className="w-6 h-6" data-id="element-421" data-mp="19yzsm3fgy5m8" />,
  heroHeading: 'Hearing Assessment & Audiometry',
  intro: 'Audiometry is a hearing assessment used to evaluate how well a person can hear different sounds and frequencies. Our hearing assessment services support early identification of hearing difficulties and appropriate referral or follow-up care.',
  overviewTitle: 'What Is Audiometry?',
  overview: 'Audiometry is a simple, painless test that checks your hearing ability across a range of pitches (frequencies) and volumes (sound levels). The results show which sounds you can hear comfortably and which may be harder for you to pick up. This helps identify hearing difficulties early, so the right advice, follow-up, or referral can be arranged.',
  benefitsTitle: 'Who May Benefit?',
  benefits: ['People experiencing difficulty hearing', 'People who frequently ask others to repeat themselves', 'Children with concerns about hearing or speech development', 'People exposed to loud environments', 'Individuals requiring hearing assessment for occupational purposes'],
  process: [{
    title: 'A Short Chat',
    desc: 'We ask about your hearing concerns, general health, and any exposure to loud noise.'
  }, {
    title: 'Getting Comfortable',
    desc: 'You sit in a quiet area and wear headphones. The test is painless and nothing goes inside the ear canal.'
  }, {
    title: 'Listening for Sounds',
    desc: 'You hear a series of tones at different pitches and volumes, and simply signal each time you hear one.'
  }, {
    title: 'Your Results',
    desc: 'We explain your results in plain language and discuss any follow-up care or referral if needed.'
  }],
  faqs: [],
  ctaTitle: 'Book a Hearing Assessment',
  ctaLabel: 'Book Audiometry Appointment',
  whatsappLabel: 'WhatsApp Us'
}, {
  id: 'spirometry',
  slug: 'spirometry',
  title: 'Spirometry',
  shortDesc: 'Breathing tests that measure how much air you breathe in and out and how quickly you exhale.',
  icon: <Wind className="w-6 h-6" data-id="element-422" data-mp="186l4nkcmig1y" />,
  heroHeading: 'Lung Function Assessment',
  intro: 'Spirometry is a breathing test that measures how much air you can breathe in and out and how quickly you can exhale. It can help healthcare professionals assess lung function and investigate certain breathing-related concerns.',
  overviewTitle: 'What Is Spirometry?',
  overview: 'Spirometry is one of the most common tests used to check how well your lungs are working. By breathing into a small device, you give your healthcare provider useful information about how much air your lungs can hold and how easily air moves out of them. The results are considered alongside your symptoms and medical history as part of your overall health assessment.',
  benefitsTitle: 'Who May Need Spirometry?',
  benefits: ['People experiencing persistent coughing', 'People experiencing shortness of breath', 'People with wheezing', 'People with known or suspected respiratory conditions', 'People requiring occupational lung-function screening', 'People requiring assessment of lung function as part of their medical evaluation'],
  process: [{
    title: 'Before the Test',
    desc: 'We ask about your breathing, general health, and any medication you use. Wear comfortable clothing that does not restrict your chest.'
  }, {
    title: 'Getting Ready',
    desc: 'You sit upright and a soft clip may be placed on your nose so all the air goes through your mouth.'
  }, {
    title: 'The Breathing Test',
    desc: 'You take a deep breath in, seal your lips around a mouthpiece, and blow out as hard and fast as you can. This is usually repeated a few times.'
  }, {
    title: 'Your Results',
    desc: 'Your healthcare provider reviews the results with you and explains any next steps or follow-up.'
  }],
  faqs: [],
  ctaTitle: 'Book a Lung Function Assessment',
  ctaLabel: 'Book Spirometry Appointment',
  whatsappLabel: 'WhatsApp Us'
}];